import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "@contexts/AuthContext";
import { FeedbackProvider } from "@contexts/FeedbackContext";
import { UnsavedChangesProvider } from "@contexts/UnsavedChangesContext";
import { NotificacoesProvider } from "@contexts/NotificacoesContext";
// import { useAuth } from '@hooks/useAuth'; // Desabilitado temporariamente
import { ProtectedRoute } from "@components/ProtectedRoute";
import { Permission, UserRole } from "@models/auth.types";

// Pages
import { Clientes } from "./pages/Clientes";
import { Login } from "./pages/Login";
import { AcessoNegado } from "./pages/AcessoNegado";
import { WrikeCallback } from "./pages/WrikeCallback";

// Pages
import { Fornecedores } from "./pages/Fornecedores";
import { Planilhas } from "./pages/Planilhas";
import NovaPlanilha from "./pages/NovaPlanilha";
import AdminPanel from "./pages/Admin/AdminPanel";
import { Usuarios } from "./pages/Admin/Usuarios";
import { Perfil } from "./pages/Perfil";
import { PlanilhaInsights } from "./pages/PlanilhaInsights";
// import OrcamentoFornecedor from "./pages/OrcamentoFornecedor";
import { PreenchimentoOrcamento } from "./pages/PreenchimentoOrcamento";
import { RecusarOrcamento } from "./pages/RecusarOrcamento";
import Header from "./components/Header";
import EditarPlanilhaV3 from "./pages/NovaPlanilha/EditarPlanilhaV3";

const NavBar: React.FC = () => {
  const location = useLocation();

  // Ocultar navbar nas rotas de orçamento do fornecedor e rotas de autenticação
  if (
    location.pathname.startsWith("/orcamento/") ||
    location.pathname.startsWith("/preenchimento-orcamento/") ||
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/auth/")
  ) {
    return null;
  }

  return <Header />;
};

function App() {
  const AppContent: React.FC = () => {
    const location = useLocation();
    const isPlanilhaFullWidth =
      location.pathname.startsWith("/planilhas/editar/") ||
      location.pathname.startsWith("/planilhas-nova/editar/") ||
      location.pathname.startsWith("/login") ||
      location.pathname.startsWith("/auth/callback") ||
      location.pathname.startsWith("/preenchimento-orcamento/");

    return (
      <div className="min-h-screen flex flex-col bg-base-200">
        {!location.pathname.startsWith("/login") &&
          !location.pathname.startsWith("/auth/callback") && <NavBar />}

        <main
          className={`flex-1 ${
            isPlanilhaFullWidth
              ? "max-w-full w-full p-0"
              : "max-w-7xl w-full mx-auto px-4 md:px-8 py-4 md:py-8"
          }`}
        >
          <Routes>
            {/* Rotas Públicas */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/wrike/callback" element={<WrikeCallback />} />
            <Route path="/auth/callback" element={<WrikeCallback />} />
            <Route path="/acesso-negado" element={<AcessoNegado />} />

            {/* Rota Pública - Fornecedor responde orçamento (ANTIGO - Manter para compatibilidade) */}
            <Route
              path="/orcamento/:token"
              element={<PreenchimentoOrcamento />}
            />

            {/* Rota Pública - Fornecedor recusa orçamento */}
            <Route
              path="/orcamento/:token/rejeitar"
              element={<RecusarOrcamento />}
            />
            {/* <Route path="/orcamento/:token" element={<OrcamentoFornecedor />} /> */}

            {/* Rotas Protegidas */}
            <Route path="/" element={<Navigate to="/planilhas" replace />} />

            <Route
              path="/clientes"
              element={
                <ProtectedRoute
                  requiredPermissions={[Permission.CLIENTES_VIEW]}
                >
                  <Clientes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/fornecedores"
              element={
                <ProtectedRoute
                  requiredPermissions={[Permission.FORNECEDORES_VIEW]}
                >
                  <Fornecedores />
                </ProtectedRoute>
              }
            />

            <Route
              path="/planilhas"
              element={
                <ProtectedRoute
                  requiredPermissions={[Permission.PLANILHAS_VIEW]}
                >
                  <Planilhas />
                </ProtectedRoute>
              }
            />

            <Route
              path="/planilhas/nova"
              element={
                <ProtectedRoute
                  requiredPermissions={[Permission.PLANILHAS_CREATE]}
                >
                  <NovaPlanilha />
                </ProtectedRoute>
              }
            />

            <Route
              path="/planilhas/editar/:id"
              element={
                <ProtectedRoute
                  requiredPermissions={[Permission.PLANILHAS_VIEW]}
                >
                  <EditarPlanilhaV3 />
                </ProtectedRoute>
              }
            />
            {/* <Route
              path="/planilhas-nova/editar/:id"
              element={
                <ProtectedRoute
                  requiredPermissions={[Permission.PLANILHAS_VIEW]}
                >
                  <EditarPlanilhaV3 />
                </ProtectedRoute>
              }
            /> */}

            {/* Painel de insights — somente leitura, exige apenas ver planilhas */}
            <Route
              path="/planilhas/:id/insights"
              element={
                <ProtectedRoute
                  requiredPermissions={[Permission.PLANILHAS_VIEW]}
                >
                  <PlanilhaInsights />
                </ProtectedRoute>
              }
            />

            {/* Perfil do próprio usuário — qualquer pessoa autenticada */}
            <Route
              path="/perfil"
              element={
                <ProtectedRoute requiredPermissions={[]}>
                  <Perfil />
                </ProtectedRoute>
              }
            />

            {/* Fallback para rotas não encontradas */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.EDITOR]}>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/usuarios"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                  <Usuarios />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    );
  };

  return (
    <AuthProvider>
      <FeedbackProvider>
        <Router>
          <UnsavedChangesProvider>
            <NotificacoesProvider>
              <AppContent />
            </NotificacoesProvider>
          </UnsavedChangesProvider>
        </Router>
      </FeedbackProvider>
    </AuthProvider>
  );
}

export default App;
